import { useCallback, useEffect, useRef } from 'react';

/**
 * Custom hook to create a debounced callback function.
 * @param callback - The function to debounce.
 * @param delay - The delay in milliseconds for the debounce.
 * @param dependencies - An array of dependencies that, when changed, will recreate the debounced function.
 * @returns A debounced version of the provided callback function.
 *
 * @remarks
 * Ensure that the `dependencies` array contains stable references (e.g., memoized functions or state values).
 * Unstable dependencies may cause the debounced function to be recreated on every dependencies changes, leading to
 * unnecessary re-creation and defeating the purpose of the debounce.
 */

const useDebouncedCallback = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
  dependencies: any[] = [],
): T & { cancel: () => void } => {
  const timeoutRef = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const debouncedFunction = useCallback(
    (...args: Parameters<T>) => {
      cancel();
      timeoutRef.current = window.setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [delay, cancel, ...dependencies],
  ) as T & { cancel: () => void };

  debouncedFunction.cancel = cancel;

  useEffect(() => {
    return cancel;
  }, [cancel]);

  return debouncedFunction;
};

export default useDebouncedCallback;
